import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useAtrasado } from "../componentes/atraso.js";
import { CabecalhoDePagina } from "../componentes/CabecalhoDePagina.js";
import { Link, useNavigate } from "react-router";
import type { DevidaHoje, ErradaPendente, PainelDeRevisao, TipoItem } from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";
import { useAbaAtual, type Aba } from "../componentes/Abas.js";
import type { Glifo } from "../componentes/glifos.js";
import { NOME_DO_TIPO, enunciadoLegivel } from "../rotulos.js";
import { PerguntarAIa } from "../componentes/PerguntarAIa.js";
import { Icone } from "../componentes/Icone.js";
import { IconeAbrirMenu, IconeConferido, IconeDevido, IconeErro, IconeOrdenar, IconeSeguir } from "../componentes/icones.js";
import { useTrilhaAtiva } from "../estado/trilhas.js";

/**
 * Revisar — as duas listas do ciclo de erro (tela 3 do SPEC).
 *
 * **Elas são coisas diferentes e ficam separadas de propósito.** A fila de
 * erradas é dívida: entrou ao errar e só sai ao acertar (RN-11), sem prazo. A
 * revisão do dia é calendário: o SM-2 marcou para hoje (RN-13). Um item pode
 * estar nas duas, e juntá-las esconderia exatamente esse caso — o que você
 * errou e ainda por cima vence hoje.
 */

type Estado =
  | { nome: "carregando" }
  | { nome: "pronto"; painel: PainelDeRevisao }
  | { nome: "erro"; mensagem: string };

/**
 * As duas listas, e a aba mora em `?aba=` como em toda tela de abas (RN-60).
 *
 * Ela morava num `useState` local — a única sobrevivente disso depois da fusão
 * de telas. Voltar, recarregar e mandar o link perdiam a aba, e o `Abas` que
 * este arquivo emprestou às outras telas existe justamente para isso não
 * acontecer.
 *
 * A contagem não cabe aqui porque depende do painel carregado: ela é somada na
 * hora de desenhar.
 */
const ABAS: readonly Aba[] = [
  { id: "erradas", rotulo: "Erradas", glifo: IconeErro },
  { id: "devidas", rotulo: "Vencem hoje", glifo: IconeDevido },
];

type IdDaAba = "erradas" | "devidas";

export function Revisar() {
  const trilha = useTrilhaAtiva();
  const navegar = useNavigate();
  const [estado, setEstado] = useState<Estado>({ nome: "carregando" });
  const mostrarEsqueleto = useAtrasado(estado.nome === "carregando");
  const [abaBruta, escolherAba] = useAbaAtual(ABAS);
  const aba = abaBruta as IdDaAba;
  const [montando, setMontando] = useState(false);

  const carregar = useCallback(async () => {
    setEstado({ nome: "carregando" });
    try {
      setEstado({ nome: "pronto", painel: await pedir("revisao.painel") });
    } catch (e) {
      setEstado({ nome: "erro", mensagem: e instanceof FalhaDaApi ? e.message : "Não foi possível carregar." });
    }
  }, []);

  useEffect(() => {
    if (trilha) void carregar();
  }, [trilha, carregar]);

  async function comecar(modo: "erradas" | "devidas") {
    setMontando(true);
    try {
      const r = await pedir("estudo.montar", {
        corpo:
          modo === "erradas"
            ? { soErradosAntes: true, priorizarMaisErrados: true, contexto: "erradas" }
            : { soDevidosHoje: true, contexto: "revisao" },
      });
      if (r.montada) void navegar("/estudar");
    } finally {
      setMontando(false);
    }
  }

  if (!trilha) {
    return (
      <Moldura>
        <section className="estado">
          <h2 className="estado__titulo">Nenhuma trilha em uso</h2>
          <p className="estado__texto">A fila de erradas e a revisão do dia são de uma trilha por vez.</p>
          <Link className="botao" to="/trilhas">
            Escolher uma trilha
          </Link>
        </section>
      </Moldura>
    );
  }

  if (estado.nome === "carregando") {
    return (
      <Moldura>
        <div aria-busy="true" data-esqueleto={mostrarEsqueleto ? "visivel" : "mudo"}>
          <span className="so-leitor" role="status">
            Carregando a revisão
          </span>
          <div className="esqueleto esqueleto--faixa" />
        </div>
      </Moldura>
    );
  }

  if (estado.nome === "erro") {
    return (
      <Moldura>
        <section className="estado estado--erro" role="alert">
          <h2 className="estado__titulo">Não deu para carregar</h2>
          <p className="estado__texto">{estado.mensagem}</p>
          <button className="botao" type="button" onClick={() => void carregar()}>
            Tentar de novo
          </button>
        </section>
      </Moldura>
    );
  }

  const p = estado.painel;
  const erradas = aba === "erradas";
  const total = erradas ? p.erradas.length : p.devidas.length;

  return (
    <div className="revisar">
      <CabecalhoDePagina
        titulo="Revisar"
        subtitulo={erradas ? "Sai da lista no primeiro acerto." : "O que o intervalo marcou para hoje, as mais atrasadas primeiro."}
        acoes={
          <button
            type="button"
            className="botao"
            disabled={total === 0 || montando}
            onClick={() => void comecar(erradas ? "erradas" : "devidas")}
          >
            <Icone glifo="revisar" papel="acao" />
            {montando ? "Montando…" : erradas ? `Praticar as ${total}` : `Revisar as ${total}`}
          </button>
        }
      />

      <div className="abas-nav" role="tablist" aria-label="Filas de revisão">
        {ABAS.map((a) => {
          const ativa = a.id === aba;
          const n = a.id === "erradas" ? p.erradas.length : p.devidas.length;
          return (
            <button
              key={a.id}
              type="button"
              role="tab"
              id={`aba-${a.id}`}
              aria-selected={ativa}
              aria-controls={`fila-${a.id}`}
              className={ativa ? "aba-nav aba-nav--ativa" : "aba-nav"}
              onClick={() => escolherAba(a.id)}
            >
              {a.rotulo.toLocaleLowerCase("pt-BR")} {n}
            </button>
          );
        })}
      </div>

      <div id={`fila-${aba}`} role="tabpanel" aria-labelledby={`aba-${aba}`} className="fila" data-tour="revLista">
        {erradas ? (
          p.erradas.length === 0 ? (
            <Vazio aba="erradas" painel={p} />
          ) : (
            <ListaDeErradas erradas={p.erradas} />
          )
        ) : p.devidas.length === 0 ? (
          <Vazio aba="devidas" painel={p} />
        ) : (
          <ListaDeDevidas devidas={p.devidas} />
        )}

        {!erradas && p.devidasAoTodo > p.devidas.length && (
          <p className="t-ajuda">
            Mostrando {p.devidas.length} das {p.devidasAoTodo} vencidas, as mais atrasadas primeiro. O teto é de{" "}
            {p.maximoRevisoesDia} por dia e fica em <Link to="/ajustes">Ajustes</Link>.
          </p>
        )}

        {p.semAgendamento > 0 && (
          <p className="t-ajuda">
            {p.semAgendamento} {p.semAgendamento === 1 ? "item ainda não entrou" : "itens ainda não entraram"} no
            intervalo: eles começam a ser agendados na primeira vez que você responde.
          </p>
        )}
      </div>
    </div>
  );
}

function CartaoDeFila({
  id,
  ativa,
  aoEscolher,
  glifo,
  nome,
  descricao,
  total,
  detalhe,
  acao,
  montando,
  aoComecar,
}: {
  id: IdDaAba;
  ativa: boolean;
  aoEscolher: () => void;
  glifo: Glifo;
  nome: string;
  descricao: string;
  total: number;
  detalhe: string;
  acao: string;
  montando: boolean;
  aoComecar: () => void;
}) {
  return (
    <section className={ativa ? "fila-cartao fila-cartao--ativa" : "fila-cartao"}>
      <button
        type="button"
        aria-pressed={ativa}
        className="fila-cartao__escolha"
        onClick={aoEscolher}
      >
        <span className="fila-cartao__nome">
          <Icone glifo={glifo} papel="estado" tamanho="p" />
          {nome}
        </span>
        <span className="fila-cartao__descricao">{descricao}</span>
      </button>
      <p className="fila-cartao__total dado">
        {total}
        <span className="so-leitor"> {total === 1 ? "item" : "itens"}</span>
      </p>
      <p className="fila-cartao__detalhe">{detalhe}</p>
      <button
        type="button"
        className={ativa ? "botao botao--compacto fila-cartao__acao" : "botao botao--secundario botao--compacto fila-cartao__acao"}
        disabled={total === 0 || montando}
        onClick={aoComecar}
      >
        {montando && ativa ? "Montando…" : acao}
        <Icone glifo={IconeSeguir} papel="acao" tamanho="p" />
      </button>
    </section>
  );
}

/** "FMC II 9 · Probabilidade 3": quantos itens da fila são de cada matéria. */
function resumoPorMateria(erradas: readonly ErradaPendente[]): string {
  const porMateria = new Map<string, number>();
  for (const e of erradas) {
    const m = e.categoria[0] ?? "Sem matéria";
    porMateria.set(m, (porMateria.get(m) ?? 0) + 1);
  }
  if (porMateria.size === 0) return "Nada pendente";
  return [...porMateria].map(([m, n]) => `${m} ${n}`).join(" · ");
}

function agrupar<T>(itens: readonly T[], chave: (t: T) => string): [string, T[]][] {
  const grupos = new Map<string, T[]>();
  for (const i of itens) {
    const k = chave(i);
    const g = grupos.get(k);
    if (g) g.push(i);
    else grupos.set(k, [i]);
  }
  return [...grupos];
}

type OrdemDasErradas = "score" | "parado" | "recente";

const ORDENS_DAS_ERRADAS: Record<OrdemDasErradas, { rotulo: string; comparar: (a: ErradaPendente, b: ErradaPendente) => number }> = {
  // A ordem do servidor já é a do score de reincidência (RN-45).
  score: { rotulo: "Mais erros primeiro", comparar: () => 0 },
  parado: { rotulo: "Parado há mais tempo", comparar: (a, b) => b.diasParado - a.diasParado },
  recente: { rotulo: "Errado mais recente", comparar: (a, b) => b.ultimoErroEm.localeCompare(a.ultimoErroEm) },
};

function ListaDeErradas({ erradas }: { erradas: readonly ErradaPendente[] }) {
  const [materia, setMateria] = useState<string | null>(null);
  const [ordem, setOrdem] = useState<OrdemDasErradas>("score");
  const materias = agrupar(erradas, (e) => e.categoria[0] ?? "Sem matéria");
  const filtradas = [...erradas]
    .filter((e) => materia === null || (e.categoria[0] ?? "Sem matéria") === materia)
    .sort(ORDENS_DAS_ERRADAS[ordem].comparar);

  return (
    <>
      <FiltrosDaFila
        titulo="Na fila de erradas"
        opcoes={materias.map(([m, lista]) => ({ id: m, rotulo: m, contagem: lista.length }))}
        escolhida={materia}
        aoEscolher={setMateria}
        ordem={ordem}
        ordens={ORDENS_DAS_ERRADAS}
        aoOrdenar={(o) => setOrdem(o as OrdemDasErradas)}
      />
      <ul className="lista-revisar cartao-mao">
        {filtradas.map((e) => (
          <LinhaErrada key={e.itemId} errada={e} />
        ))}
      </ul>
    </>
  );
}

type FiltroDasDevidas = "todas" | "atrasadas" | TipoItem;
type OrdemDasDevidas = "atraso" | "assunto";

const ORDENS_DAS_DEVIDAS: Record<OrdemDasDevidas, { rotulo: string; comparar: (a: DevidaHoje, b: DevidaHoje) => number }> = {
  atraso: { rotulo: "Mais atrasadas primeiro", comparar: (a, b) => b.atrasoDias - a.atrasoDias },
  assunto: { rotulo: "Por assunto", comparar: (a, b) => a.categoria.join(" › ").localeCompare(b.categoria.join(" › ")) },
};

function ListaDeDevidas({ devidas }: { devidas: readonly DevidaHoje[] }) {
  const [filtro, setFiltro] = useState<FiltroDasDevidas | null>(null);
  const [ordem, setOrdem] = useState<OrdemDasDevidas>("atraso");
  const atrasadas = devidas.filter((d) => d.atrasoDias > 0).length;
  const tipos = agrupar(devidas, (d) => d.tipo);
  const opcoes = [
    ...(atrasadas > 0 ? [{ id: "atrasadas", rotulo: "Atrasadas", contagem: atrasadas }] : []),
    ...tipos.map(([t, lista]) => ({ id: t, rotulo: NOME_DO_TIPO[t as TipoItem], contagem: lista.length })),
  ];
  const filtradas = [...devidas]
    .filter((d) =>
      filtro === null ? true : filtro === "atrasadas" ? d.atrasoDias > 0 : d.tipo === filtro,
    )
    .sort(ORDENS_DAS_DEVIDAS[ordem].comparar);

  return (
    <>
      <FiltrosDaFila
        titulo="Vencem hoje"
        opcoes={opcoes}
        escolhida={filtro}
        aoEscolher={(f) => setFiltro(f as FiltroDasDevidas | null)}
        ordem={ordem}
        ordens={ORDENS_DAS_DEVIDAS}
        aoOrdenar={(o) => setOrdem(o as OrdemDasDevidas)}
      />
      <ul className="lista-revisar cartao-mao">
        {filtradas.map((d) => (
          <LinhaDevida key={d.itemId} devida={d} />
        ))}
      </ul>
    </>
  );
}

function FiltrosDaFila({
  titulo,
  opcoes,
  escolhida,
  aoEscolher,
  ordem,
  ordens,
  aoOrdenar,
}: {
  titulo: string;
  opcoes: { id: string; rotulo: string; contagem: number }[];
  escolhida: string | null;
  aoEscolher: (id: string | null) => void;
  ordem: string;
  ordens: Record<string, { rotulo: string }>;
  aoOrdenar: (o: string) => void;
}) {
  return (
    <div className="fila__filtros">
      <div className="fila-mao" role="group" aria-label={`Filtrar: ${titulo}`}>
        {opcoes.length > 1 && (
          <>
            <button type="button" className="chip-mao" aria-pressed={escolhida === null} onClick={() => aoEscolher(null)}>
              todas
            </button>
            {opcoes.map((o) => (
              <button
                key={o.id}
                type="button"
                className="chip-mao"
                aria-pressed={escolhida === o.id}
                onClick={() => aoEscolher(escolhida === o.id ? null : o.id)}
              >
                {o.rotulo} <span className="t-2">({o.contagem})</span>
              </button>
            ))}
          </>
        )}
      </div>
      <label className="fila__ordem">
        <span className="t-anotacao-p">ordem</span>
        <select className="fila__ordem-campo" value={ordem} onChange={(e) => aoOrdenar(e.target.value)}>
          {Object.entries(ordens).map(([id, o]) => (
            <option key={id} value={id}>
              {o.rotulo.toLocaleLowerCase("pt-BR")}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function Grupo({ titulo, contagem, children }: { titulo: string; contagem: number; children: ReactNode }) {
  const [aberto, setAberto] = useState(true);
  return (
    <section className="fila-grupo">
      <h2 className="fila-grupo__titulo">
        <button type="button" className="fila-grupo__alternar" aria-expanded={aberto} onClick={() => setAberto(!aberto)}>
          <Icone glifo={IconeAbrirMenu} papel="acao" tamanho="p" className="fila-grupo__seta" />
          <span className="etiqueta">{titulo}</span>
          <span className="contagem-pilula">{contagem}</span>
        </button>
      </h2>
      {aberto && <ul className="fila-grupo__lista">{children}</ul>}
    </section>
  );
}

/** Uma bolinha por tentativa: os erros primeiro, depois os acertos. */
/** O histórico do item em vistos e xis: os erros primeiro (a API não guarda a
    sequência, ADR 039), no máximo oito marcas. */
function Historico({ erros, tentativas }: { erros: number; tentativas: number }) {
  const acertos = Math.max(0, tentativas - erros);
  const marcas = [...Array.from({ length: erros }, () => "x" as const), ...Array.from({ length: acertos }, () => "v" as const)].slice(0, 8);
  return (
    <span className="historico-mao" aria-label={`${erros} de ${tentativas} ${erros === 1 ? "errada" : "erradas"}`} role="img">
      {marcas.map((m, i) =>
        m === "x" ? (
          <Icone key={i} glifo="x" papel="acao" className="historico-mao__x" />
        ) : (
          <Icone key={i} glifo="check" papel="acao" className="historico-mao__v" />
        ),
      )}
    </span>
  );
}

function LinhaErrada({ errada }: { errada: ErradaPendente }) {
  return (
    <li className="linha-revisar">
      <div className="linha-revisar__corpo">
        <p className="t-anotacao-p">{errada.categoria.join(", ")}</p>
        <p className="t-corpo linha-revisar__enunciado">{enunciadoLegivel(errada.enunciado)}</p>
      </div>
      <div className="linha-revisar__quando">
        <Historico erros={errada.erros} tentativas={errada.tentativas} />
        <p className="t-anotacao-p">
          {errada.diasParado === 0 ? "errou hoje" : `parado há ${errada.diasParado} ${errada.diasParado === 1 ? "dia" : "dias"}`}
        </p>
      </div>
      <div className="linha-revisar__ia">
        <PerguntarAIa itemId={errada.itemId} modo="erro" />
      </div>
    </li>
  );
}

function LinhaDevida({ devida }: { devida: DevidaHoje }) {
  return (
    <li className="linha-revisar">
      <div className="linha-revisar__corpo">
        <p className="t-anotacao-p">
          {devida.categoria.join(", ")} · {NOME_DO_TIPO[devida.tipo].toLocaleLowerCase("pt-BR")}
        </p>
        <p className="t-corpo linha-revisar__enunciado">{enunciadoLegivel(devida.enunciado)}</p>
      </div>
      <div className="linha-revisar__quando">
        <p className={devida.atrasoDias > 0 ? "t-anotacao-p t-vermelho" : "t-anotacao-p"}>
          {devida.atrasoDias > 0 ? `atrasado ${devida.atrasoDias} ${devida.atrasoDias === 1 ? "dia" : "dias"}` : "vence hoje"}
        </p>
      </div>
    </li>
  );
}

function Moldura({ children }: { children: ReactNode }) {
  return (
    <div className="revisar">
      <CabecalhoDePagina titulo="Revisar" />
      <div className="linha-vazia" />
      {children}
    </div>
  );
}

/** Estado vazio com frase e ação — e a frase muda conforme o motivo (invariante 3). */
function Vazio({ aba, painel }: { aba: "erradas" | "devidas"; painel: PainelDeRevisao }) {
  if (aba === "erradas") {
    return (
      <section className="estado">
        <h2 className="estado__titulo">Nenhuma errada pendente</h2>
        <p className="estado__texto">
          Um item entra nesta lista quando você erra, e só sai quando você acerta. Vazia significa que não há dívida
          aberta.
        </p>
        <Link className="botao" to="/estudar">
          Estudar
        </Link>
      </section>
    );
  }
  return (
    <section className="estado">
      <h2 className="estado__titulo">Nada vence hoje</h2>
      <p className="estado__texto">
        {painel.agendados === 0
          ? "Nenhum item tem intervalo ainda: o agendamento começa na primeira vez que você responde cada um."
          : `Os ${painel.agendados} itens já agendados voltam em datas futuras. O intervalo de cada um aparece no feedback quando você responde.`}
      </p>
      <Link className="botao" to="/estudar">
        Estudar outros
      </Link>
    </section>
  );
}
